import React from 'react'
import classnames from 'classnames'
import Datepicker from 'react-tailwindcss-datepicker'
import { CalendarIcon, ChartBarSquareIcon, ClockIcon, FunnelIcon } from '@heroicons/react/24/outline'
import type { DateRange, PeriodOption } from '../screens/detail/periodRange'
import { partitionPeriodOptions } from '../screens/dashboard/dashboardPeriod'

// Combined Period + Filters + Chart View bar — borderless panel (pt-3 pb-3 px-0, gap-4,
// teal section badges) shared by Dashboard and Shadow Billing.

export type SaberView = 'daily' | 'monthly'

/** Teal accent used for section badges and header date icons (matches prototype). */
export const SABER_TEAL_ACCENT = 'text-teal-700 dark:text-teal-400'

const SECTION_BADGE =
  'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold ' +
  'bg-teal-50 text-teal-700 dark:bg-[#0d2d2d] dark:text-teal-400'

const SectionBadge: React.FC<{ icon?: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className={SECTION_BADGE}>
    {icon}
    <span>{label}</span>
  </div>
)

const BADGE_ICON = 'h-3.5 w-3.5 shrink-0'

const SectionDivider: React.FC = () => (
  <div className="hidden w-px self-stretch bg-slate-200 dark:bg-slate-600 sm:block" aria-hidden />
)

export interface SaberControlBarProps {
  periods: PeriodOption[]
  period: string
  onPeriodChange: (key: string) => void
  customRange?: DateRange | null
  onCustomRangeChange?: (range: DateRange) => void
  view?: SaberView
  onViewChange?: (view: SaberView) => void
  /** Property / Utility / Investor / Status fields — rendered in the filter column group. */
  filters?: React.ReactNode
  /** Extra actions beside filters (e.g. Clear filters). */
  filterActions?: React.ReactNode
  customRangeTestId?: string
  /** Datepicker popover. Default down; use up when the bar sits at the bottom of a panel. */
  popoverDirection?: 'up' | 'down'
}

export const CONTROL_BAR_CONTAINER = '!mt-0 pt-3 pb-3 px-0'

export const periodChipClass = (active: boolean): string =>
  classnames(
    'rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
    active
      ? 'bg-indigo-600 text-white dark:bg-indigo-500'
      : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600 dark:hover:bg-slate-700'
  )

const pad2 = (n: number): string => String(n).padStart(2, '0')
const toLocalDate = (s?: string | null): Date | null => (s != null && s !== '' ? new Date(`${s}T00:00:00`) : null)
const toYmd = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

const renderPeriodChip = (p: PeriodOption, period: string, onPeriodChange: (key: string) => void): React.ReactNode => {
  if (p.key === 'custom') return null
  return (
    <button
      key={p.key}
      type="button"
      onClick={() => onPeriodChange(p.key)}
      aria-pressed={period === p.key}
      className={periodChipClass(period === p.key)}
    >
      {p.label}
    </button>
  )
}

const renderCustomRangePicker = (
  period: string,
  onPeriodChange: (key: string) => void,
  customRange: DateRange | null | undefined,
  onCustomRangeChange: ((range: DateRange) => void) | undefined,
  customRangeTestId: string,
  popoverDirection: 'up' | 'down'
): React.ReactNode => {
  if (onCustomRangeChange == null) return null
  return (
    <div
      key="custom-range"
      className="w-56"
      data-testid={customRangeTestId}
      onFocusCapture={() => {
        if (period !== 'custom') onPeriodChange('custom')
      }}
    >
      <Datepicker
        useRange
        asSingle={false}
        value={{ startDate: toLocalDate(customRange?.from), endDate: toLocalDate(customRange?.to) }}
        onChange={(v) => {
          if (v?.startDate != null && v?.endDate != null) {
            if (period !== 'custom') onPeriodChange('custom')
            onCustomRangeChange({ from: toYmd(v.startDate), to: toYmd(v.endDate) })
          }
        }}
        primaryColor="indigo"
        popoverDirection={popoverDirection}
        separator="–"
        displayFormat="MMM D, YYYY"
        placeholder="Pick a custom range"
        toggleIcon={() => <CalendarIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" />}
        inputClassName="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
      />
    </div>
  )
}

export const SaberControlBar: React.FC<SaberControlBarProps> = ({
  periods,
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
  view,
  onViewChange,
  filters,
  filterActions,
  customRangeTestId = 'custom-range-picker',
  popoverDirection = 'down'
}) => {
  const { quarters, months, custom } = partitionPeriodOptions(periods)

  return (
  <div className={CONTROL_BAR_CONTAINER}>
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Period">
        <SectionBadge icon={<ClockIcon className={BADGE_ICON} aria-hidden />} label="Period" />
        <div className="flex flex-wrap items-center gap-1">
          {quarters.map((p) => renderPeriodChip(p, period, onPeriodChange))}
          {custom != null &&
            renderCustomRangePicker(
              period,
              onPeriodChange,
              customRange,
              onCustomRangeChange,
              customRangeTestId,
              popoverDirection
            )}
        </div>
      </div>

      {months.length > 0 && (
        <>
          <SectionDivider />
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Month">
            <SectionBadge icon={<CalendarIcon className={BADGE_ICON} aria-hidden />} label="Month" />
            <div className="flex flex-wrap items-center gap-1">
              {months.map((p) => renderPeriodChip(p, period, onPeriodChange))}
            </div>
          </div>
        </>
      )}

      {filters != null && (
        <>
          <SectionDivider />
          <div className="flex flex-wrap items-center gap-4" role="group" aria-label="Filters">
            <SectionBadge icon={<FunnelIcon className={BADGE_ICON} aria-hidden />} label="Filters" />
            {filters}
            {filterActions}
          </div>
        </>
      )}

      {view != null && onViewChange != null && (
        <>
          <SectionDivider />
          <div className="flex items-center gap-2" role="group" aria-label="Chart View">
            <SectionBadge icon={<ChartBarSquareIcon className={BADGE_ICON} aria-hidden />} label="View" />
            <div className="flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-600">
              {(['daily', 'monthly'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onViewChange(v)}
                  aria-pressed={view === v}
                  className={classnames(
                    'px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                    view === v
                      ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                      : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  </div>
  )
}

export default SaberControlBar
